import React, { Component } from "react";
import PropTypes from "prop-types";
import {
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Button,
    Collapse,
    Typography
} from "material-ui";
import { ModeEdit } from "material-ui-icons";
import { grey, red } from "material-ui/colors";
import PostHeader from "./PostHeader";
import PostEditor from "./PostEditor";
import Correction from "./Correction";
import ReactionButton from "./ReactionButton";
import CommentButton from "./CommentButton";
import CommentEditor from "./CommentEditor";
import { validateCommentText } from "../utils/validation";
import { withStylesAndTranslation } from "../utils/container";

const styleSheet = theme => ({
    leftIcon: {
        marginRight: theme.spacing.unit,
        display: "none"
    },
    "@media (min-width: 400px)": {
        leftIcon: {
            display: "inline-block"
        }
    },
    card: {
        maxWidth: 400
    },
    commentEditorContainer: {
        margin: `${theme.spacing.unit}px ${2 * theme.spacing.unit}px 0 ${2 *
            theme.spacing.unit}px`
    },
    expand: {
        transform: "rotate(0deg)",
        transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest
        })
    },
    expandOpen: {
        transform: "rotate(180deg)"
    },
    avatar: {
        backgroundColor: grey[500]
    },
    flexGrow: {
        flex: "1 1 auto"
    },
    reactionCount: {
        marginLeft: theme.spacing.unit * 2
    },
    content: {
        padding: `0 ${theme.spacing.unit * 3}px`,
        lineHeight: "1.6rem"
    },
    correctionsButtonContainer: {
        marginLeft: 1.5 * theme.spacing.unit
    },
    showCorrectionsContainer: {
        display: "flex",
        alignItems: "center",
        margin: `${theme.spacing.unit}px ${theme.spacing.unit}px ${-0.5 *
            theme.spacing.unit}px ${2 * theme.spacing.unit}px`
    },
    commentsActions: {
        justifyContent: "flex-end"
    },
    buttonProgress: {
        marginLeft: theme.spacing.unit,
        color: grey[500]
    },
    error: {
        color: red[500],
        marginTop: -2
    }
});

class Post extends Component {
    corrections = {};

    constructor(props) {
        super(props);
        this.state = {
            showComment: false,
            showCorrections:
                !!props.defaultShowCorrectionId ||
                props.post.corrections.count() === 0,
            submittingComment: false,
            currentCommentText: "",
            allowScroll: true
        };
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }

    renderCorrectionsButton(onCorrectionButtonClick) {
        const { classes, t } = this.props;
        return (
            <Button dense={true} onClick={onCorrectionButtonClick}>
                <ModeEdit className={classes.leftIcon} />{" "}
                {t("post.Add correction")}
            </Button>
        );
    }

    componentWillReceiveProps(nextProps) {
        // If we receive a new comment, reset the state of the post.
        if (
            nextProps.post.corrections.count() >
            this.props.post.corrections.count()
        ) {
            this.setState({
                showComment: false,
                submittingComment: false,
                currentCommentText: "",
                allowScroll: true
            });
        }
    }

    renderReactionsButton(post) {
        const { currentUser, onReactionButtonClick } = this.props;

        const numReactions = post.reactions.count();
        const active = !!post.reactions.find(r => r.userId === currentUser.id);
        return (
            <ReactionButton
                active={active}
                numReactions={numReactions}
                onReactionButtonClick={() =>
                    onReactionButtonClick({
                        post,
                        active,
                        userId: currentUser.id
                    })}
            />
        );
    }

    onToggleViewCorrections = () => {
        const { showCorrections } = this.state;
        this.setState({ showCorrections: !showCorrections });
    };

    renderViewCorrections(post) {
        const { t } = this.props;
        const { corrections } = post;
        if (corrections.count() > 0) {
            const options = {
                count: corrections.count()
            };
            const { showCorrections } = this.state;
            return (
                <Button
                    dense={true}
                    onClick={this.onToggleViewCorrections}
                    color="primary">
                    {showCorrections
                        ? t("post.Hide feedback")
                        : t("post.Show all feedback", options)}
                </Button>
            );
        }
    }

    determineScroll = id => () => {
        if (
            id === this.props.defaultShowCorrectionId &&
            this.corrections[id] &&
            this.corrections[id].scrollIntoView
        ) {
            this.corrections[id].scrollIntoView(true);
        }
    };

    renderCorrections() {
        const { currentUser } = this.props;
        const { corrections } = this.props.post;

        return (
            <div>
                {corrections.map(correction => {
                    return (
                        <Correction
                            rootRef={r => {
                                this.corrections[correction.id] = r;
                                setTimeout(
                                    this.determineScroll(correction.id),
                                    0
                                );
                            }}
                            onMenuItemClick={this.onCorrectionMenuItemClick}
                            currentUserId={currentUser.id}
                            onReactionButtonClick={
                                this.props.onCorrectionReactionButtonClick
                            }
                            key={correction.id}
                            correction={correction}
                            edit={false}
                        />
                    );
                })}
            </div>
        );
    }

    renderCommentsButton() {
        const { post } = this.props;
        const numComments = post.corrections.filter(c => !!c.comment).count();
        return (
            <CommentButton
                onClick={this.showCommentEditor}
                numComments={numComments}
            />
        );
    }

    showCommentEditor = () => {
        this.setState({ showComment: true });
    };

    onCloseComment = () => {
        this.setState({ showComment: false });
    };

    onSubmitComment = () => {
        const { onSubmitComment, post } = this.props;
        const { currentCommentText } = this.state;

        onSubmitComment({ post, text: currentCommentText });

        // HACK: We're going to fake the loading state and just
        // show loading for at most 5 seconds.
        this.setState({ submittingComment: true }, () => {
            this.timer = setTimeout(() => {
                this.setState({
                    submittingComment: false
                });
            }, 5000);
        });
    };

    onChangeComment = newBody => {
        this.setState({ currentCommentText: newBody });
    };

    renderCommentEditor() {
        const {
            t,
            classes,
            currentUser: { displayName, avatarURL }
        } = this.props;

        const { currentCommentText } = this.state;

        return (
            <div className={classes.commentEditorContainer}>
                <CommentEditor
                    onChange={this.onChangeComment}
                    text={currentCommentText}
                    placeholder={t("post.Add a comment")}
                    displayName={displayName}
                    avatarURL={avatarURL}
                />
            </div>
        );
    }

    renderCardActions() {
        const { t, post, onCorrectionButtonClick, classes } = this.props;
        const {
            currentCommentText,
            showComment,
            submittingComment
        } = this.state;
        if (showComment) {
            const { valid, error } = validateCommentText(currentCommentText);
            return (
                <CardActions className={classes.commentsActions}>
                    {error &&
                        <Typography className={classes.error} type="body1">
                            {error}
                        </Typography>}
                    <Button onClick={this.onCloseComment}>
                        {t("post.Close")}
                    </Button>
                    <Button
                        disabled={submittingComment || !valid}
                        onClick={this.onSubmitComment}>
                        {t("post.Save")}
                        {submittingComment &&
                            <CircularProgress
                                size={24}
                                className={classes.buttonProgress}
                            />}
                    </Button>
                </CardActions>
            );
        }
        return (
            <CardActions>
                <div
                    className={classes.correctionsButtonContainer}
                    style={{ flex: 1 }}>
                    {this.renderCorrectionsButton(onCorrectionButtonClick)}
                </div>
                <div>
                    {this.renderCommentsButton()}
                </div>
                <div style={{ marginRight: 16 }}>
                    {this.renderReactionsButton(post)}
                </div>
            </CardActions>
        );
    }

    getHeaderMenuItemConfig() {
        const { post, currentUser } = this.props;
        const copyLinkToShare = {
            text: "post.Copy link to share",
            action: "copyLink"
        };

        if (currentUser.id === post.author.id) {
            return [
                copyLinkToShare,
                {
                    text: "post.Edit",
                    action: "edit"
                },
                {
                    text: "post.Delete",
                    action: "delete"
                }
            ];
        }
        return [
            copyLinkToShare,
            {
                text: "post.Report abuse",
                action: "report"
            }
        ];
    }

    onMenuItemClick = action => {
        const { post, onMenuClick } = this.props;
        onMenuClick({ action, post });
    };

    onCorrectionMenuItemClick = payload => {
        const { onCorrectionMenuItemClick } = this.props;
        onCorrectionMenuItemClick(payload);
    };

    render() {
        const { post, classes } = this.props;
        const { showComment } = this.state;
        const { author } = post;
        return (
            <Card elevation={1}>
                <PostHeader
                    user={author}
                    postId={post.id}
                    onMenuItemClick={this.onMenuItemClick}
                    date={post.creationDate}
                    menuItemConfig={this.getHeaderMenuItemConfig()}
                />
                <CardContent className={classes.content}>
                    <PostEditor readOnly={true} text={post.body} />
                </CardContent>
                <div className={classes.showCorrectionsContainer}>
                    {this.renderViewCorrections(post)}
                </div>
                <Collapse in={this.state.showCorrections} unmountOnExit>
                    {this.renderCorrections()}
                </Collapse>
                {showComment && this.renderCommentEditor()}
                {this.renderCardActions()}
            </Card>
        );
    }
}

Post.propTypes = {
    post: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    classes: PropTypes.object,
    onCorrectionButtonClick: PropTypes.func,
    onReactionButtonClick: PropTypes.func,
    onCorrectionReactionButtonClick: PropTypes.func,
    onSubmitComment: PropTypes.func,
    onCorrectionMenuItemClick: PropTypes.func,
    onMenuClick: PropTypes.func,
    defaultShowCorrectionId: PropTypes.string,
    t: PropTypes.func.isRequired
};

Post.defaultProps = {
    onCorrectionButtonClick: () => {},
    onReactionButtonClick: () => {},
    onCorrectionReactionButtonClick: () => {},
    onSubmitComment: () => {},
    onMenuClick: () => {},
    onCorrectionMenuItemClick: () => {},
    defaultShowCorrectionId: null
};

export default withStylesAndTranslation(Post, styleSheet);



// WEBPACK FOOTER //
// ./src/components/Post.jsx